<script setup lang="ts">
/**
 * 챗봇 입력창 '+' 버튼으로 여는 타겟 멀티셀렉트 팝오버.
 * (feature: chatbot-target-conversation)
 *
 * 구조: 검색(자동완성) → 카테고리 필터 칩 → [핀][최근][목록] 섹션.
 * 목록은 IntersectionObserver sentinel 로 lazy 무한 로딩(서버 offset paging).
 * 선택/핀/최근은 chat store(localStorage 영속)에 위임.
 *
 * 컴포넌트 미재사용 사유: 공용 VirtualScrollSelect 는 자기완결형 드롭다운 select 라
 * (자체 fixed 드롭박스·클라이언트 필터·bottom-reach 이벤트 부재) 패널 내 임베드 +
 * 서버 lazy + 멀티 섹션 요건에 맞지 않음. EVUI 프리미티브(ev-icon/ev-text-field)로 구성.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useChatStore } from '@/ai/store/chat';
import { useMyInfoStore } from '@/config/stores/my-info';
import {
  fetchTargetCatalog,
  fetchTargetsByIds,
  type TargetCatalogItem,
} from '@/ai/utils/targetCatalog';
import type { SelectedTarget } from '@/ai/utils/streamChatbot';
import { useInternational } from '@/common/locale';

const emit = defineEmits<{ (e: 'close'): void }>();

const { t } = useInternational();

const chatStore = useChatStore();
const { selectedTargets, pinnedTargetIds, recentTargetIds } = storeToRefs(chatStore);
const { myInfo: userInfo } = storeToRefs(useMyInfoStore());

/** 카테고리 그룹 — label + 매핑 target_kind 들(콤마로 backend category 전송). */
const CATEGORY_GROUPS: { key: string; label: string; kinds: string[] }[] = [
  { key: 'all', label: t('WORD.ALL'), kinds: [] },
  { key: 'host', label: t('WORD.HOST'), kinds: ['host', 'host_port', 'filesystem'] },
  {
    key: 'database',
    label: t('WORD.DATABASE'),
    kinds: [
      'database',
      'database_cluster',
      'oracle',
      'mysql',
      'postgresql',
      'sqlserver',
      'tibero',
      'cubrid',
      'altibase',
      'mongodb',
      'redis',
      'clickhouse',
    ],
  },
  {
    key: 'application',
    label: t('WORD.APPLICATION'),
    kinds: [
      'application',
      'java',
      'dotnet',
      'python',
      'nodejs',
      'webserver',
      'url',
      'rum',
      'process',
    ],
  },
  {
    key: 'kubernetes',
    label: t('WORD.KUBERNETES'),
    kinds: [
      'pod',
      'node',
      'cluster',
      'namespace',
      'deployment',
      'statefulset',
      'daemonset',
      'replicaset',
      'cronjob',
      'job',
      'service',
      'container',
      'configmap',
      'hpa',
      'ingress',
    ],
  },
  {
    key: 'cloud',
    label: t('WORD.CLOUD'),
    kinds: [
      'cloud',
      'aws_account',
      'aws_ec2',
      'aws_rds',
      'aws_aurora',
      'aws_dynamodb',
      'aws_redshift',
      'aws_documentdb',
      'aws_s3',
      'aws_lambda',
      'aws_vpc',
      'azure_virtual_machines',
      'azure_functions',
      'azure_blob_storage',
      'azure_cosmos_db',
      'azure_sql_database',
      'azure_database_for_postgresql',
      'azure_database_for_mysql',
    ],
  },
  {
    key: 'network',
    label: t('WORD.NETWORK'),
    kinds: ['network_device', 'network_object', 'interface'],
  },
  {
    key: 'messagequeue',
    label: t('WORD.CHATBOT.TARGET.KIND_MQ'),
    kinds: [
      'kafka_cluster',
      'kafka_broker',
      'kafka_zookeeper',
      'kafka_broker_topic',
      'kafka_cluster_topic',
    ],
  },
];

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

const searchInput = ref<string>('');
const query = ref<string>('');
const activeCategory = ref<string>('all');
/** '활성만 보기' 토글 — ON 시 서버에서 active 타겟만 반환. */
const showActiveOnly = ref<boolean>(false);

const items = ref<TargetCatalogItem[]>([]);
const offset = ref<number>(0);
const hasMore = ref<boolean>(false);
const loading = ref<boolean>(false);

const pinnedItems = ref<TargetCatalogItem[]>([]);
const recentItems = ref<TargetCatalogItem[]>([]);
const capNotice = ref<boolean>(false);

const rootRef = ref<HTMLDivElement | null>(null);
const listRef = ref<HTMLDivElement | null>(null);
const sentinelRef = ref<HTMLDivElement | null>(null);
const searchRef = ref<HTMLInputElement | null>(null);

const userIdStr = computed<string>(() => String(userInfo.value?.userId ?? -1));

/** 핀/최근 추천 섹션은 기본 브라우즈(검색X · 전체 카테고리)에서만 노출. */
const showSections = computed<boolean>(() => !query.value && activeCategory.value === 'all');

/** 기본 브라우즈에서는 메인 목록에서 핀·최근 중복 제거. */
const mainItems = computed<TargetCatalogItem[]>(() => {
  if (!showSections.value) return items.value;
  const top = new Set<string>([...pinnedTargetIds.value, ...recentTargetIds.value]);
  return items.value.filter((it) => !top.has(it.targetId));
});

const categoryParam = (): string | undefined => {
  const group = CATEGORY_GROUPS.find((g) => g.key === activeCategory.value);
  if (!group || group.key === 'all' || !group.kinds.length) return undefined;
  return group.kinds.join(',');
};

const toSelectedTarget = (it: TargetCatalogItem): SelectedTarget => ({
  targetId: it.targetId,
  name: it.name,
  kind: it.kind,
  subKind: it.subKind || undefined,
  alarmLevel: it.alarmLevel,
});

const isSelected = (targetId: string): boolean => chatStore.isTargetSelected(targetId);
const isPinned = (targetId: string): boolean => chatStore.isTargetPinned(targetId);

/** 라이브 상태 배지 라벨/클래스 (active/inactive/noSignal). status 없으면(미지원 kind) 배지 미표시. */
const statusLabel = (s?: string): string =>
  s === 'active'
    ? t('WORD.CHATBOT.TARGET.STATUS_ACTIVE')
    : s === 'inactive'
      ? t('WORD.CHATBOT.TARGET.STATUS_INACTIVE')
      : s === 'noSignal'
        ? t('WORD.CHATBOT.TARGET.STATUS_NO_SIGNAL')
        : '';
const statusClass = (s?: string): string =>
  s === 'active'
    ? 'target-picker__status--active'
    : s === 'inactive'
      ? 'target-picker__status--inactive'
      : s === 'noSignal'
        ? 'target-picker__status--nosignal'
        : '';

// 요청 토큰 — 카테고리/검색을 빠르게 바꿔도 '마지막' reloadList 응답만 반영(stale 덮어쓰기 방지).
// loadMore 는 현재 컨텍스트 토큰을 캡처해, 도중 reload(컨텍스트 변경) 가 끼면 페이지 결과를 폐기.
let listSeq = 0;
// maybeFillViewport 자동 보충 상한 — dedupe 로 가시 행이 안 늘어도 무한 페이지 요청 방지.
let autoFillCount = 0;
const MAX_AUTOFILL = 20;

/** 목록 첫 페이지 로드(카테고리/검색 변경 시 리셋). */
const reloadList = async (): Promise<void> => {
  const mySeq = ++listSeq;
  autoFillCount = 0;
  loading.value = true;
  offset.value = 0;
  const { items: rows, hasMore: more } = await fetchTargetCatalog({
    userId: userIdStr.value,
    category: categoryParam(),
    q: query.value,
    active: showActiveOnly.value,
    offset: 0,
    size: PAGE_SIZE,
  });
  if (mySeq !== listSeq) return; // 더 늦은 reload 가 시작됨 — stale 응답 무시(loading 은 최신이 관리)
  items.value = rows;
  hasMore.value = more;
  loading.value = false;
  // 첫 페이지가 컨테이너를 못 채우면(스크롤 불가) sentinel 이 안 떠 lazy 가 멈추므로 보충.
  await nextTick();
  maybeFillViewport();
};

const loadMore = async (): Promise<void> => {
  if (loading.value || !hasMore.value) return;
  const mySeq = listSeq;
  loading.value = true;
  const next = offset.value + PAGE_SIZE;
  const { items: rows, hasMore: more } = await fetchTargetCatalog({
    userId: userIdStr.value,
    category: categoryParam(),
    q: query.value,
    active: showActiveOnly.value,
    offset: next,
    size: PAGE_SIZE,
  });
  if (mySeq !== listSeq) return; // reload(컨텍스트 변경) 가 끼어듦 — 이 페이지 결과 폐기
  items.value = [...items.value, ...rows];
  offset.value = next;
  hasMore.value = more;
  loading.value = false;
  await nextTick();
  maybeFillViewport();
};

/** 컨테이너가 스크롤되지 않을 만큼 짧으면(=sentinel 비가시) 다음 페이지를 미리 당겨 채운다. */
const maybeFillViewport = (): void => {
  const el = listRef.value;
  if (!el || !hasMore.value || loading.value) return;
  if (autoFillCount >= MAX_AUTOFILL) return; // dedupe 로 가시 행이 안 늘면 무한 보충 방지(스크롤 시 sentinel 로 재개)
  if (el.scrollHeight <= el.clientHeight + 4) {
    autoFillCount += 1;
    loadMore();
  }
};

const loadPinnedRecent = async (): Promise<void> => {
  const [pins, recents] = await Promise.all([
    fetchTargetsByIds(userIdStr.value, pinnedTargetIds.value),
    fetchTargetsByIds(userIdStr.value, recentTargetIds.value),
  ]);
  // 핀/최근 표시 순서를 저장된 id 순서대로 정렬.
  const order = (ids: string[], rows: TargetCatalogItem[]) =>
    ids.map((id) => rows.find((r) => r.targetId === id)).filter((r): r is TargetCatalogItem => !!r);
  pinnedItems.value = order(pinnedTargetIds.value, pins);
  recentItems.value = order(recentTargetIds.value, recents);
};

const onToggleSelect = (it: TargetCatalogItem): void => {
  const ok = chatStore.toggleSelectTarget(toSelectedTarget(it));
  if (!ok) {
    capNotice.value = true;
    window.setTimeout(() => {
      capNotice.value = false;
    }, 2500);
  }
};

const onTogglePin = (it: TargetCatalogItem): void => {
  chatStore.togglePinTarget(it.targetId);
  loadPinnedRecent();
};

// 검색 디바운스
let searchTimer: ReturnType<typeof setTimeout> | null = null;
watch(searchInput, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    query.value = val.trim();
  }, SEARCH_DEBOUNCE_MS);
});
watch([query, activeCategory, showActiveOnly], () => {
  reloadList();
});

const selectCategory = (key: string): void => {
  if (activeCategory.value === key) return;
  activeCategory.value = key;
};

// lazy — IntersectionObserver sentinel
let observer: IntersectionObserver | null = null;
const setupObserver = (): void => {
  if (!sentinelRef.value || !listRef.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    },
    { root: listRef.value, rootMargin: '120px' },
  );
  observer.observe(sentinelRef.value);
};

// 바깥 클릭 / ESC 닫기
const onDocMouseDown = (e: MouseEvent): void => {
  const el = e.target as HTMLElement | null;
  if (rootRef.value && el && rootRef.value.contains(el)) return;
  // '+' 버튼 클릭은 토글이 처리 — 여기서 닫으면 close→reopen 으로 엇갈리므로 제외.
  if (el && el.closest('.chat-window__target-btn')) return;
  emit('close');
};
const onDocKeydown = (e: KeyboardEvent): void => {
  if (e.key === 'Escape') {
    e.stopPropagation();
    emit('close');
  }
};

onMounted(async () => {
  // 영속값을 최신 userId 로 재로딩(레이어 진입 시점 보장).
  chatStore.loadPersistedTargets();
  await Promise.all([reloadList(), loadPinnedRecent()]);
  setupObserver();
  // 바깥/ESC 리스너 — capture 로 textarea 보다 우선 ESC 처리.
  document.addEventListener('mousedown', onDocMouseDown, true);
  document.addEventListener('keydown', onDocKeydown, true);
  await nextTick();
  searchRef.value?.focus?.();
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
  if (searchTimer) clearTimeout(searchTimer);
  document.removeEventListener('mousedown', onDocMouseDown, true);
  document.removeEventListener('keydown', onDocKeydown, true);
});

const levelClass = (level: string): string => {
  if (level === 'CRITICAL') return 'target-picker__level--critical';
  if (level === 'WARNING') return 'target-picker__level--warning';
  return 'target-picker__level--normal';
};

/** 행 좌측 accent — CRITICAL/WARNING 타겟을 한눈에('분석 필요' 시각 단서). */
const rowLevelClass = (level: string): string => {
  if (level === 'CRITICAL') return 'target-picker__row--critical';
  if (level === 'WARNING') return 'target-picker__row--warning';
  return '';
};

const selectedCount = computed<number>(() => selectedTargets.value.length);
</script>

<template>
  <div
    ref="rootRef"
    class="target-picker"
  >
    <div class="target-picker__header">
      <span class="target-picker__title">{{ t('WORD.SELECT_TARGET') }}</span>
      <span
        v-if="selectedCount"
        class="target-picker__count"
        >{{ selectedCount }} / {{ chatStore.MAX_SELECTED_TARGETS }}</span
      >
      <button
        v-if="selectedCount"
        type="button"
        class="target-picker__clear"
        @click="chatStore.clearSelectedTargets()"
      >
        {{ t('WORD.CHATBOT.TARGET.DESELECT_ALL') }}
      </button>
      <button
        type="button"
        class="target-picker__close"
        :aria-label="t('WORD.CLOSE')"
        @click="emit('close')"
      >
        <ev-icon icon="icon-close" />
      </button>
    </div>

    <div class="target-picker__search">
      <ev-icon
        icon="ev-icon-search"
        class="target-picker__search-icon"
      />
      <input
        ref="searchRef"
        v-model="searchInput"
        type="text"
        class="target-picker__search-input"
        :placeholder="t('WORD.CHATBOT.TARGET.SEARCH_PLACEHOLDER')"
      />
    </div>

    <div class="target-picker__categories">
      <button
        v-for="group in CATEGORY_GROUPS"
        :key="group.key"
        type="button"
        class="target-picker__chip"
        :class="{ 'target-picker__chip--active': activeCategory === group.key }"
        @click="selectCategory(group.key)"
      >
        {{ group.label }}
      </button>
    </div>

    <div class="target-picker__filters">
      <label class="target-picker__active-toggle">
        <input
          v-model="showActiveOnly"
          type="checkbox"
          class="target-picker__active-toggle-input"
        />
        <span
          class="target-picker__active-toggle-track"
          aria-hidden="true"
        ></span>
        <span class="target-picker__active-toggle-text">{{
          t('WORD.CHATBOT.TARGET.ACTIVE_ONLY')
        }}</span>
      </label>
    </div>

    <p
      v-if="capNotice"
      class="target-picker__cap-notice"
    >
      {{ t('WORD.CHATBOT.TARGET.MAX_LIMIT', { max: chatStore.MAX_SELECTED_TARGETS }) }}
    </p>

    <div
      ref="listRef"
      class="target-picker__body"
    >
      <!-- 핀 섹션 -->
      <template v-if="showSections && pinnedItems.length">
        <div class="target-picker__section-title">
          {{ t('WORD.CHATBOT.TARGET.SECTION_PINNED') }}
        </div>
        <button
          v-for="it in pinnedItems"
          :key="`pin-${it.targetId}`"
          type="button"
          class="target-picker__row"
          :class="{ 'target-picker__row--selected': isSelected(it.targetId) }"
          @click="onToggleSelect(it)"
        >
          <span
            class="target-picker__check"
            :class="{ 'target-picker__check--on': isSelected(it.targetId) }"
          >
            <ev-icon
              v-if="isSelected(it.targetId)"
              icon="ev-icon-check-on"
            />
          </span>
          <span class="target-picker__name">{{ it.name }}</span>
          <span class="target-picker__kind"
            >{{ it.kind }}{{ it.subKind ? `/${it.subKind}` : '' }}</span
          >
          <span
            v-if="it.status"
            class="target-picker__status"
            :class="statusClass(it.status)"
            >{{ statusLabel(it.status) }}</span
          >
          <span
            v-if="it.alarmLevel !== 'NORMAL'"
            class="target-picker__level"
            :class="levelClass(it.alarmLevel)"
            >{{ it.alarmLevel }}</span
          >
          <span
            class="target-picker__pin target-picker__pin--on"
            role="button"
            :aria-label="t('WORD.CHATBOT.TARGET.UNPIN')"
            @click.stop="onTogglePin(it)"
          >
            <ev-icon icon="icon-bookmark" />
          </span>
        </button>
      </template>

      <!-- 최근 섹션 -->
      <template v-if="showSections && recentItems.length">
        <div class="target-picker__section-title">
          {{ t('WORD.CHATBOT.TARGET.SECTION_RECENT') }}
        </div>
        <button
          v-for="it in recentItems"
          :key="`recent-${it.targetId}`"
          type="button"
          class="target-picker__row"
          :class="{ 'target-picker__row--selected': isSelected(it.targetId) }"
          @click="onToggleSelect(it)"
        >
          <span
            class="target-picker__check"
            :class="{ 'target-picker__check--on': isSelected(it.targetId) }"
          >
            <ev-icon
              v-if="isSelected(it.targetId)"
              icon="ev-icon-check-on"
            />
          </span>
          <span class="target-picker__name">{{ it.name }}</span>
          <span class="target-picker__kind"
            >{{ it.kind }}{{ it.subKind ? `/${it.subKind}` : '' }}</span
          >
          <span
            v-if="it.status"
            class="target-picker__status"
            :class="statusClass(it.status)"
            >{{ statusLabel(it.status) }}</span
          >
          <span
            v-if="it.alarmLevel !== 'NORMAL'"
            class="target-picker__level"
            :class="levelClass(it.alarmLevel)"
            >{{ it.alarmLevel }}</span
          >
          <span
            class="target-picker__pin"
            :class="{ 'target-picker__pin--on': isPinned(it.targetId) }"
            role="button"
            :aria-label="t('WORD.CHATBOT.TARGET.PIN')"
            @click.stop="onTogglePin(it)"
          >
            <ev-icon icon="icon-bookmark" />
          </span>
        </button>
      </template>

      <!-- 메인 목록 -->
      <div
        v-if="showSections && (pinnedItems.length || recentItems.length)"
        class="target-picker__section-title"
      >
        {{ t('WORD.ALL') }}
      </div>
      <button
        v-for="it in mainItems"
        :key="it.targetId"
        type="button"
        class="target-picker__row"
        :class="[
          { 'target-picker__row--selected': isSelected(it.targetId) },
          rowLevelClass(it.alarmLevel),
        ]"
        @click="onToggleSelect(it)"
      >
        <span
          class="target-picker__check"
          :class="{ 'target-picker__check--on': isSelected(it.targetId) }"
        >
          <ev-icon
            v-if="isSelected(it.targetId)"
            icon="ev-icon-check-on"
          />
        </span>
        <span class="target-picker__name">{{ it.name }}</span>
        <span class="target-picker__kind"
          >{{ it.kind }}{{ it.subKind ? `/${it.subKind}` : '' }}</span
        >
        <span
          v-if="it.status"
          class="target-picker__status"
          :class="statusClass(it.status)"
          >{{ statusLabel(it.status) }}</span
        >
        <span
          v-if="it.alarmLevel !== 'NORMAL'"
          class="target-picker__level"
          :class="levelClass(it.alarmLevel)"
          >{{ it.alarmLevel }}</span
        >
        <span
          class="target-picker__pin"
          :class="{ 'target-picker__pin--on': isPinned(it.targetId) }"
          role="button"
          :aria-label="t('WORD.CHATBOT.TARGET.PIN')"
          @click.stop="onTogglePin(it)"
        >
          <ev-icon icon="icon-bookmark" />
        </span>
      </button>

      <div
        v-if="!loading && !mainItems.length && !pinnedItems.length && !recentItems.length"
        class="target-picker__empty"
      >
        {{ t('WORD.CHATBOT.TARGET.EMPTY_FILTERED') }}
      </div>

      <div
        v-if="loading"
        class="target-picker__loading"
      >
        {{ t('WORD.CHATBOT.LOADING') }}
      </div>

      <!-- lazy sentinel -->
      <div
        ref="sentinelRef"
        class="target-picker__sentinel"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.target-picker {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 400px;
  padding: 10px;
  overflow: hidden;
  border: 1px solid var(--color-gray-05-03);
  border-radius: 12px;
  background-color: var(--color-gray-09-00);
  box-shadow: 0 12px 32px -8px rgb(0, 0, 0, 0.28);

  &__header {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 0 2px 8px;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: var(--main-font-color);
  }

  &__count {
    padding: 1px 8px;
    border-radius: 10px;
    background: var(--color-blue-09-01);
    font-size: 11px;
    font-weight: 600;
    color: var(--color-blue-04-04);
  }

  &__clear {
    margin-left: auto;
    cursor: pointer;
    border: none;
    background: none;
    font-size: 12px;
    color: var(--color-blue-04-04);

    &:hover {
      text-decoration: underline;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    padding: 3px;
    margin-left: auto;
    cursor: pointer;
    border: none;
    border-radius: 6px;
    background: none;
    color: var(--color-gray-03-11);

    &:hover {
      background: var(--color-gray-05-03);
      color: var(--main-font-color);
    }
  }

  // clear 가 있으면 clear 가 우측 정렬을 가져가고 close 는 바로 옆에 붙는다.
  &__clear + &__close {
    margin-left: 8px;
  }

  &__search {
    display: flex;
    gap: 6px;
    align-items: center;
    padding: 6px 10px;
    margin-bottom: 8px;
    border: 1px solid var(--color-gray-05-03);
    border-radius: 10px;
    background: var(--color-gray-08-01);
    transition: border-color 0.15s ease;

    &:focus-within {
      border-color: var(--color-blue-04-04);
    }
  }
  &__search-icon {
    color: var(--color-gray-03-11);
  }
  &__search-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: var(--main-font-color);

    &::placeholder {
      color: var(--color-gray-03-11);
    }
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 8px;
  }

  &__chip {
    padding: 3px 11px;
    cursor: pointer;
    border: 1px solid var(--color-gray-05-03);
    border-radius: 13px;
    background: transparent;
    font-size: 12px;
    color: var(--color-gray-02-11);
    transition:
      border-color 0.12s ease,
      background 0.12s ease,
      color 0.12s ease;

    &:hover {
      border-color: var(--color-blue-04-04);
    }
    &--active {
      border-color: var(--color-blue-04-04);
      background: var(--color-blue-04-04);
      color: #ffffff;
    }
  }

  &__cap-notice {
    padding: 4px 8px;
    margin: 0 0 6px;
    border-radius: 6px;
    background: var(--color-red-09-09);
    font-size: 12px;
    color: var(--color-red-05-05);
  }

  &__filters {
    display: flex;
    align-items: center;
    padding: 2px 4px 8px;
  }

  &__active-toggle {
    display: inline-flex;
    gap: 7px;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }

  &__active-toggle-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }

  &__active-toggle-track {
    display: inline-block;
    position: relative;
    width: 30px;
    height: 17px;
    border-radius: 9px;
    background: var(--color-gray-05-03);
    transition: background 0.15s ease;

    &::after {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background: #ffffff;
      transition: transform 0.15s ease;
      content: '';
    }
  }

  &__active-toggle-input:checked + &__active-toggle-track {
    background: var(--color-blue-04-04);

    &::after {
      transform: translateX(13px);
    }
  }

  &__active-toggle-text {
    font-size: 12px;
    color: var(--color-gray-03-11);
  }

  &__body {
    flex: 1;
    padding: 0 4px;
    margin: 0 -4px;
    overflow-y: auto;
  }

  &__section-title {
    padding: 8px 4px 3px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--color-gray-03-11);
    text-transform: uppercase;
  }

  &__row {
    display: flex;
    gap: 9px;
    align-items: center;
    width: 100%;
    padding: 7px 8px;
    cursor: pointer;
    border: none;
    border-left: 2px solid transparent;
    border-radius: 7px;
    background: none;
    font-size: 13px;
    color: var(--main-font-color);
    text-align: left;
    transition: background 0.1s ease;

    &:hover {
      background: var(--color-gray-08-01);
    }
    &--selected {
      background: var(--color-blue-09-01);
    }

    // '분석 필요' 좌측 accent — CRITICAL/WARNING 을 한눈에.
    &--critical {
      border-left-color: var(--color-red-05-05);
    }
    &--warning {
      // yellow-03-07 은 light 에서 #b45309(적갈색)라 critical 붉은 계열과 붙어 보임 → 양테마 공용 앰버로 고정
      border-left-color: var(--color-yellow-05-05);
    }
  }

  &__check {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 17px;
    height: 17px;
    border: 1.5px solid var(--color-gray-04-04);
    border-radius: 5px;
    color: #ffffff;
    transition:
      border-color 0.1s ease,
      background 0.1s ease;

    .ev-icon-check-on {
      width: 12px;
      height: 12px;
    }
    &--on {
      border-color: var(--color-blue-04-04);
      background: var(--color-blue-04-04);
    }
  }

  &__name {
    flex: 1;
    overflow: hidden;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__kind {
    flex-shrink: 0;
    padding: 1px 7px;
    border-radius: 6px;
    background: var(--color-gray-07-00);
    font-size: 10px;
    color: var(--color-gray-03-11);
  }

  &__level {
    flex-shrink: 0;
    padding: 1px 7px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;

    &--critical {
      background: var(--color-red-09-09);
      color: var(--color-red-05-05);
    }

    // critical(red-09 bg + red-05 text)·활성(green-09 bg + green-05 text)과 동일한 배지 공식으로 통일.
    // 기존 '밝은 앰버 bg + 갈색 text' 조합은 light 에서 붉게 읽혀 critical 과 구분이 어려웠다.
    &--warning {
      background: var(--color-yellow-09-09);
      color: var(--color-yellow-04-04);
    }
    &--normal {
      color: var(--color-gray-03-11);
    }
  }

  &__status {
    flex-shrink: 0;
    padding: 1px 7px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;

    &--active {
      background: var(--color-green-09-09);
      color: var(--color-green-05-05);
    }
    &--inactive {
      background: var(--color-gray-07-00);
      color: var(--color-gray-03-11);
    }
    &--nosignal {
      background: var(--color-yellow-06-04);
      color: var(--color-yellow-03-07);
    }
  }

  &__pin {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    padding: 2px;
    opacity: 0.4;
    cursor: pointer;
    border-radius: 5px;
    color: var(--color-gray-03-11);
    transition:
      opacity 0.1s ease,
      background 0.1s ease,
      color 0.1s ease;

    &:hover {
      opacity: 1;
      background: var(--color-gray-05-03);
    }
    &--on {
      opacity: 1;
      color: var(--color-blue-04-04);
    }
  }

  &__empty,
  &__loading {
    padding: 20px 8px;
    font-size: 12px;
    color: var(--color-gray-03-11);
    text-align: center;
  }

  &__sentinel {
    height: 1px;
  }
}
</style>
